import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import { useToast } from "../../hooks/useToast";

const normalizeGroupEntries = (groups) => {
  if (!Array.isArray(groups)) return [];

  return groups
    .map((group) => {
      if (!group) return null;

      if (typeof group === "string") {
        return {
          name: group,
          clientCode: null,
        };
      }

      if (typeof group === "object") {
        const nameCandidate =
          group.NOMBRE ??
          group.nombre ??
          group.name ??
          group.GRUPO ??
          group.grupo ??
          null;

        const name =
          typeof nameCandidate === "string" ? nameCandidate.trim() : null;

        if (!name) return null;

        const clientRaw =
          group.COD_CLIENTE ??
          group.cod_cliente ??
          group.COD_ID_GROUP ??
          group.cod_id_group ??
          null;

        const clientCode =
          clientRaw !== undefined && clientRaw !== null
            ? String(clientRaw)
            : null;

        return { name, clientCode };
      }

      return null;
    })
    .filter((entry) => entry && entry.name.length > 0);
};

const normalizeGroupNamesByClient = (groups, clientCode) => {
  const normalized = normalizeGroupEntries(groups);

  if (!clientCode) {
    return Array.from(
      new Set(normalized.map((group) => group.name).filter(Boolean))
    );
  }

  const targetCode = String(clientCode);

  return Array.from(
    new Set(
      normalized
        .filter((group) => {
          if (!group.clientCode) return false;
          return String(group.clientCode) === targetCode;
        })
        .map((group) => group.name)
        .filter((name) => typeof name === "string" && name.trim().length > 0)
    )
  );
};

const normalizeAllGroupNames = (groups) => {
  if (!Array.isArray(groups)) return [];
  const normalized = groups
    .map((group) => (typeof group === "string" ? group.trim() : ""))
    .filter((name) => name.length > 0);
  return Array.from(new Set(normalized));
};

function ModalUserPermissions({ user, onClose, allGroups, onSave ,selectedClientCode}) {
  const toast = useToast();
  const normalizedAllGroups = useMemo(
    () => normalizeAllGroupNames(allGroups),
    [allGroups]
  );

  const initialGroups = useMemo(
    () => normalizeGroupNamesByClient(user.GRUPOS, selectedClientCode),
    [user.GRUPOS, selectedClientCode]
  );

  const [selectedGroups, setSelectedGroups] = useState(initialGroups);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedGroups(initialGroups);
  }, [initialGroups, user.COD_ID]);

  const originalGroups = useMemo(() => initialGroups, [initialGroups]);

  const availableGroups = useMemo(() => {
    return normalizedAllGroups.filter(
      (group) => !selectedGroups.includes(group)
    );
  }, [normalizedAllGroups, selectedGroups]);

  const hasChanges = useMemo(() => {
    if (originalGroups.length !== selectedGroups.length) {
      return true;
    }

    const sortedOriginal = [...originalGroups].sort();
    const sortedSelected = [...selectedGroups].sort();

    return sortedOriginal.some((group, index) => group !== sortedSelected[index]);
  }, [originalGroups, selectedGroups]);

  const handleAddGroup = (group) => {
    const normalizedGroup =
      typeof group === "string" ? group.trim() : String(group || "").trim();

    if (!normalizedGroup) return;

    setSelectedGroups((prev) => {
      if (prev.includes(normalizedGroup)) return prev;
      return [...prev, normalizedGroup];
    });
  };

  const handleRemoveGroup = (group) => {
    const normalizedGroup =
      typeof group === "string" ? group.trim() : String(group || "").trim();

    if (!normalizedGroup) return;

    setSelectedGroups((prev) => prev.filter((g) => g !== normalizedGroup));
  };

  const handleAcceptClick = () => {
    if (!hasChanges || isSaving) return;
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!hasChanges) {
      setShowConfirm(false);
      return;
    }
     if (!selectedClientCode) {
      toast.error(
        "No se ha podido determinar el cliente asociado para actualizar los grupos."
      );
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        COD_ID: user.COD_ID,
        USER: user.USER,
        COD_ID_GROUP: selectedClientCode,
        GRUPOS: selectedGroups,
      };

      const success = await onSave(payload);

      if (success) {
        toast.success("Permisos actualizados correctamente.");
        setShowConfirm(false);
        onClose();
      } else {
        toast.error("No se pudieron actualizar los permisos del usuario.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.message || "Error al actualizar los permisos del usuario."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const acceptButtonClass = `boton_opciones${
    !hasChanges || isSaving ? " disable" : ""
  }`;

  return (
    <>
      <Modal
        title={`Permisos de Usuario`}
        onClose={onClose}
        className="modal-wide"
      >
        <div className="input-group">
          <label className="label">Usuario:</label>
          <input className="input" value={user.USER} disabled />
        </div>

        <div className="celda" style={{ display: "flex", gap: "10px" }}>
          <div className="cuadro-container">
            <label className="titulo">Grupos disponibles</label>
            <div className="cuadro">
              <div className="linea">
                {availableGroups.length > 0 ? (
                  availableGroups.map((group) => (
                    <div
                      key={group}
                      onClick={() => handleAddGroup(group)}
                      style={{
                        padding: "5px 10px",
                        cursor: "pointer",
                      }}
                    >
                      {group}
                    </div>
                  ))
                ) : (
                  <span style={{ color: "#888", fontStyle: "italic" }}>
                    No hay grupos disponibles para asignar
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="cuadro-container">
            <label className="titulo">Grupos del usuario</label>
            <div className="cuadro">
              <div className="linea">
                {selectedGroups.length > 0 ? (
                  selectedGroups.map((group) => (
                    <div
                      key={group}
                      onClick={() => handleRemoveGroup(group)}
                      style={{
                        padding: "5px 10px",
                        cursor: "pointer",
                      }}
                    >
                      {group}
                    </div>
                  ))
                ) : (
                  <span style={{ color: "#888", fontStyle: "italic" }}>
                    El usuario no tiene grupos asignados
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="input-group_row"
          style={{ justifyContent: "center", gap: "1rem" }}
        >
          <span className={acceptButtonClass} onClick={handleAcceptClick}>
            Aceptar
          </span>
          <span className="boton_cancelar" onClick={onClose}>
            Cancelar
          </span>
        </div>
      </Modal>

      {showConfirm && (
        <Modal
          title="Confirmar cambios"
          onClose={() => {
            if (isSaving) return;
            setShowConfirm(false);
          }}
          draggable={false}
          closeOnBackdropClick={!isSaving}
          closeOnEsc={!isSaving}
        >
          <p style={{ marginBottom: "1.5rem" }}>
            ¿Deseas guardar los cambios de grupos para el usuario {user.USER}?
          </p>
          <div
            className="input-group_row"
            style={{ justifyContent: "center", gap: "1rem" }}
          >
            <span
              className={`boton_opciones${isSaving ? " disable" : ""}`}
              onClick={!isSaving ? handleConfirmSave : undefined}
            >
              {isSaving ? "Guardando..." : "Aceptar"}
            </span>
            <span
              className={`boton_cancelar${isSaving ? " disable" : ""}`}
              onClick={() => {
                if (isSaving) return;
                setShowConfirm(false);
              }}
            >
              Cancelar
            </span>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ModalUserPermissions;